import { useCallback, useEffect, useState } from 'react';
import axios from '../config/api';
import { downloadMarketingSpendExcel } from '../utils/downloadMarketingSpendExcel';

interface ReportRow {
  id: string;
  cafe_name: string;
  title: string;
  status: string;
  duration_days: number;
  budget_total_jod: number;
  offer_tier?: string;
  orders_count: number;
  total_revenue: number;
}

interface MarketingReportsResponse {
  campaigns: ReportRow[];
  offers: ReportRow[];
  summary: {
    campaign_budget_total_jod: number;
    offer_budget_total_jod: number;
    campaign_count: number;
    offer_count: number;
  };
}

export function useMarketingSpendReport(selectedCafe: string, start?: Date, end?: Date) {
  const [data, setData] = useState<MarketingReportsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (selectedCafe) params.cafe_id = selectedCafe;
      if (start) params.start = start.toISOString();
      if (end) params.end = end.toISOString();
      const { data: res } = await axios.get<MarketingReportsResponse>(
        '/api/promotions/admin/marketing-reports',
        { params }
      );
      setData(res);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedCafe, start?.toISOString(), end?.toISOString()]);

  useEffect(() => {
    load();
  }, [load]);

  const downloadExcel = useCallback(() => {
    if (!data) return;
    const date = new Date().toISOString().slice(0, 10);
    downloadMarketingSpendExcel(
      `marketing-spend-${date}.xlsx`,
      data.summary,
      data.campaigns,
      data.offers,
      !selectedCafe
    );
  }, [data, selectedCafe]);

  const totalCount = (data?.summary.campaign_count ?? 0) + (data?.summary.offer_count ?? 0);

  const subtitle = loading
    ? 'Loading…'
    : error
      ? error
      : 'Campaigns on sheet 1, offers on sheet 2 (filtered by selected period).';

  return {
    loading,
    error,
    totalCount,
    subtitle,
    downloadExcel,
    downloadDisabled: loading || !!error || !data,
    reload: load,
  };
}
